import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../../helpers'
import { EntityClass } from '../../models'
import { toast } from 'react-toastify'

export const useEntityClassifications = () => {
    const queryClient = useQueryClient()

    // Query for fetching entity classifications
    const { data: entityClassifications = [], isLoading } = useQuery({
        queryKey: ['entityClassifications'],
        queryFn: async () => {
            const response = await axiosInstance.get(
                '/document-audit/entity-classes/',
            )
            return response.data as EntityClass[]
        },
        onError: () => {
            toast.error('Error fetching entity classifications')
        },
    })

    // Mutation for creating entity classification
    const createMutation = useMutation({
        mutationFn: async ({ name }: { name: string }) => {
            const response = await axiosInstance.post(
                '/document-audit/entity-classes/',
                { name },
            )
            return response.data as EntityClass
        },
        onSuccess: (newEntityClassification) => {
            queryClient.setQueryData<EntityClass[]>(
                ['entityClassifications'],
                (old = []) => [...old, newEntityClassification],
            )
        },
        onError: () => {
            toast.error('Error creating entity classification')
        },
    })

    // Mutation for updating entity classification
    const updateMutation = useMutation({
        mutationFn: async ({ id, name }: { id: number; name: string }) => {
            const response = await axiosInstance.patch(
                `/document-audit/entity-classes/${id}/`,
                { name },
            )
            return response.data as EntityClass
        },
        onSuccess: (updatedEntityClassification) => {
            queryClient.setQueryData<EntityClass[]>(
                ['entityClassifications'],
                (old = []) =>
                    old.map((classification) =>
                        classification.id === updatedEntityClassification.id
                            ? updatedEntityClassification
                            : classification,
                    ),
            )
        },
        onError: () => {
            toast.error('Error updating entity classification')
        },
    })

    return {
        isLoading,
        entityClassifications,
        fetchEntityClassifications: () =>
            queryClient.invalidateQueries({
                queryKey: ['entityClassifications'],
            }),
        createEntityClassification: createMutation.mutate,
        updateEntityClassification: updateMutation.mutate,
    }
}
